import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ButtonGroup } from "react-bootstrap";
import axiosInstance from "../../../../../services/AxiosInstance";
import { notifyError } from "jsx/components/CitoPlus/components/CitoPlusToastr";
import {
  SelectVivienda,
  FIELD_LAYOUTS,
} from "jsx/components/CitoPlus/components/FieldBasic";
import DatePickerRanger from "jsx/components/CitoPlus/components/DatePickerRanger";
import { SpinnerCitoPlus } from "jsx/components/CitoPlus/components/loadingLogo";
import Tables from "jsx/components/CitoPlus/components/tables";
import ReservaGestionModal from "./ReservaGestionModal";
import CambiarEstadoReservaModal from "./CambiarEstadoReservaModal";
import {
  CitoPlusLinkIconPrimary,
  CitoPlusLinkIconSuccess,
} from "jsx/components/CitoPlus/components/CitoPlusLink";
import {
  ZonaSocialSelect,
  useZonaSocialOptions,
} from "jsx/components/CitoPlus/components/ZonaSocial";

const RESERVAS_URL = "/api/v3/zonas-sociales/reservas/";
const EXPORT_RESERVAS_URL = "/api/v3/zonas-sociales/reservas/exportar/";

const STATUS_METADATA = Object.freeze({
  all: { label: "Todos los estados", variant: "secondary" },
  pendiente: { label: "Pendiente", variant: "warning" },
  confirmada: { label: "Confirmada", variant: "success" },
  cancelada: { label: "Cancelada", variant: "danger" },
  desconocido: { label: "Estado desconocido", variant: "secondary" },
});

const RANGE_PRESETS = Object.freeze({
  all: {
    label: "Todos",
    getRange: () => ({ start: null, end: null }),
  },
  upcoming_90: {
    label: "Próximos 90 días",
    getRange: () => {
      const now = new Date();
      const end = new Date(now.getTime());
      end.setUTCDate(end.getUTCDate() + 90);
      return { start: now, end };
    },
  },
  past_90: {
    label: "Últimos 90 días",
    getRange: () => {
      const now = new Date();
      const start = new Date(now.getTime());
      start.setUTCDate(start.getUTCDate() - 90);
      return { start, end: now };
    },
  },
  year_window: {
    label: "±180 días",
    getRange: () => {
      const now = new Date();
      const start = new Date(now.getTime());
      const end = new Date(now.getTime());
      start.setUTCDate(start.getUTCDate() - 180);
      end.setUTCDate(end.getUTCDate() + 180);
      return { start, end };
    },
  },
  custom: {
    label: "Personalizado",
    getRange: () => ({ start: null, end: null }),
  },
});

const ensureDate = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();

    const legacyMatch = trimmed.match(
      /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})$/,
    );
    if (legacyMatch) {
      const [, day, month, year, hours, minutes, seconds] = legacyMatch;
      const parsed = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hours),
        Number(minutes),
        Number(seconds),
      );
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const isoParsed = new Date(trimmed);
    return Number.isNaN(isoParsed.getTime()) ? null : isoParsed;
  }

  if (typeof value === "number") {
    const fromNumber = new Date(value);
    return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
  }

  try {
    const fallback = new Date(value);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  } catch (_error) {
    return null;
  }
};

const normalizeReservation = (raw, unidadLabelFallback) => {
  if (!raw || !raw.uuid) {
    return null;
  }

  const start = ensureDate(raw.start ?? raw.fecha_inicio);
  const end = ensureDate(raw.end ?? raw.fecha_fin);

  if (!start || !end) {
    return null;
  }

  const status = raw.estado || "desconocido";
  const zoneName =
    raw.zona_nombre ||
    raw.zona?.nombre ||
    raw.zona?.nombre_zona ||
    "Zona social sin nombre";

  return {
    uuid: raw.uuid,
    estado: status,
    titulo: raw.titulo || zoneName,
    descripcion: raw.descripcion || "",
    start,
    end,
    zonaNombre: zoneName,
    residente:
      raw.residente_username ||
      raw.residente?.user?.username ||
      "Residente no disponible",
    unidad:
      raw.residente_vivienda ||
      raw.unidad ||
      raw.citofonia_slug ||
      unidadLabelFallback,
  };
};

const HistorialReservasAdmin = () => {
  const [unidadFilter, setUnidadFilter] = useState({
    value: "",
    label: "",
  });
  const [estadoFilter, setEstadoFilter] = useState("all");
  const [zonaFilter, setZonaFilter] = useState("all");
  const [rangePreset, setRangePreset] = useState("all");
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [reservations, setReservations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [unidadSelectKey, setUnidadSelectKey] = useState(0);

  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [nextUrl, setNextUrl] = useState(null);
  const [previousUrl, setPreviousUrl] = useState(null);

  const [sortField, setSortField] = useState("");
  const [order, setOrder] = useState("asc");
  const [modalState, setModalState] = useState({ isOpen: false, uuid: null });
  const [estadoModalState, setEstadoModalState] = useState({
    isOpen: false,
    uuid: null,
    currentEstado: "",
  });

  const { zones, isLoading: isLoadingZones } = useZonaSocialOptions({
    onError: () =>
      notifyError("No fue posible obtener el listado de zonas sociales."),
  });

  const initialFetchRef = useRef(false);

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat("es-CO", {
        dateStyle: "medium",
        timeStyle: "short",
      }),
    [],
  );

  const presetOptions = useMemo(
    () =>
      Object.entries(RANGE_PRESETS).map(([value, config]) => ({
        value,
        label: config.label,
      })),
    [],
  );

  const isResetDisabled = useMemo(
    () =>
      !unidadFilter.value &&
      estadoFilter === "all" &&
      zonaFilter === "all" &&
      rangePreset === "all" &&
      currentPage === 1 &&
      !hasSearched,
    [
      estadoFilter,
      rangePreset,
      zonaFilter,
      unidadFilter.value,
      currentPage,
      hasSearched,
    ],
  );

  const buildQueryParams = useCallback(
    (rangeOverride) => {
      const activeRange = rangeOverride ?? dateRange;
      const { start, end } = activeRange;

      if (!start && !end) {
        return {};
      }

      if (!start || !end) {
        return null;
      }

      const normalizedStart = new Date(start);
      const normalizedEnd = new Date(end);

      if (
        Number.isNaN(normalizedStart.getTime()) ||
        Number.isNaN(normalizedEnd.getTime())
      ) {
        return null;
      }

      if (normalizedStart.getTime() > normalizedEnd.getTime()) {
        return {
          start: normalizedEnd.toISOString(),
          end: normalizedStart.toISOString(),
        };
      }

      return {
        start: normalizedStart.toISOString(),
        end: normalizedEnd.toISOString(),
      };
    },
    [dateRange],
  );

  const fetchReservations = useCallback(
    async (pageIndex = 1, overrides = {}) => {
      const rangeParams = buildQueryParams(overrides.range);
      if (rangeParams === null) {
        notifyError("Selecciona un rango de fechas válido.");
        return;
      }

      setIsLoading(true);
      setHasSearched(true);

      const safePage =
        typeof totalPages === "number" && totalPages > 0
          ? Math.min(Math.max(1, pageIndex || 1), totalPages)
          : Math.max(1, pageIndex || 1);

      try {
        const params = {
          ...rangeParams,
          page: safePage,
        };

        if (unidadFilter.value) {
          params.unidad = unidadFilter.value;
        }

        if (zonaFilter !== "all") {
          params.zona = zonaFilter;
        }

        if (estadoFilter !== "all") {
          params.estado = estadoFilter;
        }

        const ordering =
          overrides.ordering ??
          (sortField
            ? `${order === "desc" ? "-" : ""}${sortField}`
            : undefined);

        if (ordering) {
          params.ordering = ordering;
        }

        const response = await axiosInstance.get(RESERVAS_URL, { params });
        const payload = Array.isArray(response.data)
          ? response.data
          : response.data?.results || [];

        const normalized = payload
          .map((item) => normalizeReservation(item, unidadFilter.label))
          .filter(Boolean);

        const count =
          typeof response.data?.count === "number"
            ? response.data.count
            : Array.isArray(response.data)
              ? response.data.length
              : normalized.length;

        const fetchedTotalPages =
          response.data?.total_pages ??
          (count > 0 ? Math.ceil(count / (response.data?.page_size ?? 25)) : 1);

        setReservations(normalized);
        setTotalRecords(count);
        setTotalPages(Math.max(fetchedTotalPages, 1));
        const resolvedPage =
          response.data?.current_page ?? response.data?.page ?? safePage;
        setCurrentPage(resolvedPage);
        setNextUrl(response.data?.next ?? null);
        setPreviousUrl(response.data?.previous ?? null);
      } catch (error) {
        notifyError(
          "No pudimos obtener el historial de reservas. Intenta de nuevo más tarde.",
        );
        setReservations([]);
        setTotalRecords(0);
        setTotalPages(1);
        setCurrentPage(1);
        setNextUrl(null);
        setPreviousUrl(null);
      } finally {
        setIsLoading(false);
      }
    },
    [
      buildQueryParams,
      estadoFilter,
      order,
      sortField,
      totalPages,
      unidadFilter.label,
      unidadFilter.value,
      zonaFilter,
    ],
  );

  const openModal = useCallback((uuid) => {
    if (!uuid) {
      return;
    }
    setModalState({ isOpen: true, uuid });
  }, []);

  const handleModalClose = useCallback(() => {
    setModalState({ isOpen: false, uuid: null });
  }, []);

  const handleModalUpdated = useCallback(() => {
    fetchReservations(currentPage);
  }, [currentPage, fetchReservations]);

  const openEstadoModal = useCallback((uuid, currentEstado) => {
    if (!uuid) {
      return;
    }
    setEstadoModalState({ isOpen: true, uuid, currentEstado });
  }, []);

  const handleEstadoModalClose = useCallback(() => {
    setEstadoModalState({ isOpen: false, uuid: null, currentEstado: "" });
  }, []);

  const handleEstadoModalUpdated = useCallback(() => {
    fetchReservations(currentPage);
  }, [currentPage, fetchReservations]);

  useEffect(() => {
    if (initialFetchRef.current) {
      return;
    }

    initialFetchRef.current = true;
    fetchReservations(1, { range: { start: null, end: null } });
  }, [fetchReservations]);

  const handleUnidadChange = useCallback((selection) => {
    const option = Array.isArray(selection) ? selection[0] : selection;

    if (!option) {
      setUnidadFilter({ value: "", label: "" });
      return;
    }

    const value =
      option.value === null || option.value === undefined
        ? ""
        : String(option.value).trim();
    const label =
      option.label === null || option.label === undefined
        ? value
        : String(option.label).trim();

    setUnidadFilter({ value, label });
  }, []);

  const handleEstadoChange = useCallback((event) => {
    setEstadoFilter(event.target.value);
  }, []);

  const handleZonaChange = useCallback((newValue) => {
    setZonaFilter(newValue || "all");
  }, []);

  const handlePresetChange = useCallback((event) => {
    const presetKey = event.target.value;
    setRangePreset(presetKey);

    if (presetKey === "custom") {
      return;
    }

    const preset = RANGE_PRESETS[presetKey];
    if (!preset) {
      return;
    }

    setDateRange(preset.getRange());
  }, []);

  const handleRangePickerChange = useCallback((values) => {
    const [start, end] = values || [];
    setRangePreset("custom");
    setDateRange({
      start: start ? new Date(start) : null,
      end: end ? new Date(end) : null,
    });
  }, []);

  const handleResetFilters = useCallback(() => {
    setUnidadFilter({ value: "", label: "" });
    setEstadoFilter("all");
    setZonaFilter("all");
    setRangePreset("all");
    setDateRange(RANGE_PRESETS.all.getRange());
    setSortField("");
    setOrder("asc");
    setUnidadSelectKey((prev) => prev + 1);
    setHasSearched(false);
    fetchReservations(1, {
      range: RANGE_PRESETS.all.getRange(),
      ordering: undefined,
    });
  }, [fetchReservations]);

  const handleConsult = useCallback(() => {
    fetchReservations(1);
  }, [fetchReservations]);

  const handleExport = useCallback(async () => {
    const rangeParams = buildQueryParams();
    if (rangeParams === null) {
      notifyError("Selecciona un rango de fechas válido.");
      return;
    }

    const params = {
      ...rangeParams,
    };

    if (unidadFilter.value) {
      params.unidad = unidadFilter.value;
    }

    if (zonaFilter !== "all") {
      params.zona = zonaFilter;
    }

    if (estadoFilter !== "all") {
      params.estado = estadoFilter;
    }

    setIsExporting(true);
    try {
      const response = await axiosInstance.get(EXPORT_RESERVAS_URL, {
        params,
        responseType: "blob",
      });

      const contentDisposition =
        response.headers?.["content-disposition"] || "";
      const filenameMatch = contentDisposition.match(
        /filename=\"?([^\";]+)\"?/,
      );
      const filename =
        (filenameMatch && filenameMatch[1]) ||
        `reservas_zonas_sociales_${Date.now()}.xlsx`;

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      notifyError(
        "No pudimos exportar el historial de reservas. Intenta de nuevo más tarde.",
      );
    } finally {
      setIsExporting(false);
    }
  }, [buildQueryParams, estadoFilter, unidadFilter.value, zonaFilter]);

  const handleSortingChange = useCallback(
    (column) => {
      if (!column?.id) {
        return;
      }

      const nextOrder =
        sortField === column.id ? (order === "asc" ? "desc" : "asc") : "asc";

      setSortField(column.id);
      setOrder(nextOrder);

      fetchReservations(1, {
        ordering: `${nextOrder === "desc" ? "-" : ""}${column.id}`,
      });
    },
    [fetchReservations, order, sortField],
  );

  const statusSummary = useMemo(() => {
    const summary = { upcoming: 0, past: 0 };
    const now = Date.now();

    reservations.forEach((reservation) => {
      if (!reservation?.end) {
        summary.upcoming += 1;
        return;
      }

      if (reservation.end.getTime() >= now) {
        summary.upcoming += 1;
      } else {
        summary.past += 1;
      }
    });

    return summary;
  }, [reservations]);

  const showEmptyState = hasSearched && !isLoading && reservations.length === 0;

  const columns = useMemo(
    () => [
      {
        Header: "Estado",
        accessor: "estado",
        disableSortBy: true,
        disableSortable: true,
        disableFilters: true,
        Cell: ({ value }) => {
          const metadata =
            STATUS_METADATA[value] || STATUS_METADATA.desconocido;
          return (
            <span className={`badge bg-${metadata.variant}`}>
              {metadata.label}
            </span>
          );
        },
      },
      {
        Header: "Detalle",
        accessor: "titulo",
        disableSortBy: true,
        disableSortable: true,
        disableFilters: true,
        Cell: ({ row }) => (
          <>
            <div className="fw-semibold">{row.original.titulo}</div>
            <div className="text-muted small">
              {row.original.descripcion || "Sin descripción"}
            </div>
          </>
        ),
      },
      {
        id: "fecha_inicio",
        Header: "Horario",
        accessor: "start",
        disableSortBy: false,
        disableSortable: false,
        disableFilters: true,
        Cell: ({ row }) => (
          <div>
            <div>
              {row.original.start
                ? dateFormatter.format(row.original.start)
                : "-"}
            </div>
            <div className="text-muted small">
              {row.original.end ? dateFormatter.format(row.original.end) : "-"}
            </div>
          </div>
        ),
      },
      {
        Header: "Zona social",
        accessor: "zonaNombre",
        disableSortBy: true,
        disableSortable: true,
        disableFilters: true,
        Cell: ({ value }) => <span className="text-nowrap">{value}</span>,
      },
      {
        Header: "Inmueble",
        accessor: "Inmueble",
        disableSortBy: true,
        disableSortable: true,
        disableFilters: true,
        Cell: ({ row }) => (
          <span className="text-nowrap">{row.original.unidad}</span>
        ),
      },
      {
        Header: "Acciones",
        accessor: "acciones",
        disableSortBy: true,
        disableSortable: true,
        disableFilters: true,
        Cell: ({ row }) => (
          <ButtonGroup aria-label="Acciones de reserva">
            <CitoPlusLinkIconPrimary
              title="Ver detalle"
              onClick={() => openModal(row.original.uuid)}
            >
              <i className="fas fa-eye" />
            </CitoPlusLinkIconPrimary>
            <CitoPlusLinkIconSuccess
              title="Cambiar estado"
              onClick={() =>
                openEstadoModal(row.original.uuid, row.original.estado)
              }
            >
              <i className="fas fa-exchange-alt" />
            </CitoPlusLinkIconSuccess>
          </ButtonGroup>
        ),
      },
    ],
    [dateFormatter, openModal, openEstadoModal],
  );

  return (
    <section className="card">
      <header className="card-header">
        <div className="d-flex flex-wrap align-items-center gap-2 w-100">
          <div className="d-flex flex-column me-auto flex-grow-1">
            <h2 className="card-title mb-0">
              Historial de reservas por inmueble
            </h2>
            <div className="text-muted small">
              Próximas: {statusSummary.upcoming.toLocaleString("es-CO")} ·
              Pasadas: {statusSummary.past.toLocaleString("es-CO")}
            </div>
          </div>
          <div className="d-flex gap-2 ms-auto">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm text-body"
              onClick={handleResetFilters}
              disabled={isLoading || isResetDisabled || isExporting}
            >
              Limpiar filtros
            </button>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm text-primary"
              onClick={handleExport}
              disabled={isLoading || isExporting}
            >
              {isExporting ? (
                <span className="d-inline-flex align-items-center gap-2">
                  <i
                    className="fas fa-circle-notch fa-spin"
                    aria-hidden="true"
                  />
                  <span>Exportando...</span>
                </span>
              ) : (
                <>
                  <i className="fas fa-file-excel me-2" />
                  Exportar XLSX
                </>
              )}
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm text-white"
              onClick={handleConsult}
              disabled={isLoading || isExporting}
            >
              {isLoading ? (
                <span className="d-inline-flex align-items-center gap-2">
                  <i
                    className="fas fa-circle-notch fa-spin"
                    aria-hidden="true"
                  />
                  <span>Consultando...</span>
                </span>
              ) : (
                <>
                  <i className="fas fa-search me-2" />
                  Consultar
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      <div className="card-body">
        <div className="row g-3 mb-4">
          <div className="col-xl-4 col-lg-5 col-md-6">
            <SelectVivienda
              key={`unidad-select-${unidadSelectKey}`}
              layout={FIELD_LAYOUTS.VERTICAL}
              showLabel
              required={false}
              label="Inmueble"
              placeholder="Selecciona un inmueble"
              onChange={handleUnidadChange}
              defaultsValues={unidadFilter.value}
              fieldClassName="mb-0"
              contentClassName="w-100"
              selectClassName="w-100"
              isClearable
              isSearchable
            />
            {unidadFilter.label && (
              <div className="form-text">
                Consultando: <strong>{unidadFilter.label}</strong>
              </div>
            )}
          </div>

          <div className="col-lg-3 col-md-6">
            <ZonaSocialSelect
              value={zonaFilter}
              onChange={handleZonaChange}
              allowAllOption
              allOptionValue="all"
              allOptionLabel="Todas las zonas"
              allowEmptyOption={false}
              showLabel
              label="Zona social"
              options={zones}
              loading={isLoadingZones}
              autoFetch={false}
              disabled={isLoadingZones || isLoading}
            />
          </div>

          <div className="col-lg-3 col-md-6">
            <label htmlFor="estado-filter" className="form-label">
              Estado
            </label>
            <select
              id="estado-filter"
              name="estado"
              className="form-select"
              value={estadoFilter}
              onChange={handleEstadoChange}
              disabled={isLoading}
            >
              {Object.entries(STATUS_METADATA).map(([key, metadata]) => (
                <option key={key} value={key}>
                  {metadata.label}
                </option>
              ))}
            </select>
          </div>

          <div className="col-lg-3 col-md-6">
            <label htmlFor="range-preset" className="form-label">
              Rango
            </label>
            <select
              id="range-preset"
              className="form-select"
              value={rangePreset}
              onChange={handlePresetChange}
              disabled={isLoading}
            >
              {presetOptions.map((preset) => (
                <option key={preset.value} value={preset.value}>
                  {preset.label}
                </option>
              ))}
            </select>
          </div>

          <div className="col-lg-4 col-md-6">
            <label className="form-label">Fechas personalizadas</label>
            <DatePickerRanger
              startDate={dateRange.start}
              endDate={dateRange.end}
              onChange={handleRangePickerChange}
              disabled={rangePreset !== "custom" || isLoading}
              withPortal
              selectsRange
              customInput={
                <input
                  className="form-control"
                  placeholder="Selecciona un rango"
                  readOnly
                />
              }
            />
            {rangePreset !== "custom" && (
              <div className="form-text">
                {rangePreset === "all"
                  ? "Mostrarás todas las reservas: no se envía rango de fechas al backend."
                  : "El rango lo fija automáticamente el preset elegido; el selector solo refleja esas fechas calculadas."}
              </div>
            )}
          </div>
        </div>

        {isLoading && (
          <div className="py-5 text-center">
            <SpinnerCitoPlus text="Cargando historial de reservas..." />
          </div>
        )}

        {!isLoading && showEmptyState && (
          <div className="alert alert-light border" role="alert">
            No encontramos reservas para los criterios seleccionados.
          </div>
        )}

        {!isLoading && !showEmptyState && reservations.length > 0 && (
          <Tables
            columns={columns}
            data={reservations}
            loading={isLoading}
            totalPages={totalPages}
            currentPage={currentPage}
            totalRecords={totalRecords}
            fetchUrl={fetchReservations}
            handleSortingChange={handleSortingChange}
            sortField={sortField}
            order={order}
            nextUrl={nextUrl}
            previousUrl={previousUrl}
          />
        )}

        {!isLoading && !hasSearched && (
          <div className="alert alert-info" role="alert">
            Ajusta los filtros que necesites y haz clic en{" "}
            <strong>Consultar</strong> para obtener el historial de reservas.
          </div>
        )}
      </div>
      <ReservaGestionModal
        show={modalState.isOpen}
        reservaUuid={modalState.uuid}
        onClose={handleModalClose}
        onUpdated={handleModalUpdated}
        cachedZones={zones}
      />
      <CambiarEstadoReservaModal
        show={estadoModalState.isOpen}
        reservaUuid={estadoModalState.uuid}
        currentEstado={estadoModalState.currentEstado}
        onClose={handleEstadoModalClose}
        onUpdated={handleModalUpdated}
      />
    </section>
  );
};

export default HistorialReservasAdmin;
